import { useEffect } from "react";

const SITE_NAME = "NixDojo";
const DEFAULT_ORIGIN = "https://nixdojo.com";
const DEFAULT_DESCRIPTION =
    "NixDojo provides hands-on Linux and DevOps labs with guided lessons, browser terminals, and task validation for practical learning.";

function upsertMeta(selector, attrs) {
    let element = document.head.querySelector(selector);
    if (!element) {
        element = document.createElement("meta");
        document.head.appendChild(element);
    }

    Object.entries(attrs).forEach(([key, value]) => {
        element.setAttribute(key, value);
    });
}

function upsertLink(rel, href) {
    let element = document.head.querySelector(`link[rel="${rel}"]`);
    if (!element) {
        element = document.createElement("link");
        element.setAttribute("rel", rel);
        document.head.appendChild(element);
    }
    element.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
    let element = document.getElementById(id);
    if (!element) {
        element = document.createElement("script");
        element.id = id;
        element.type = "application/ld+json";
        document.head.appendChild(element);
    }
    element.textContent = JSON.stringify(data);
}

export default function Seo({
    title,
    description = DEFAULT_DESCRIPTION,
    path = "/",
    image = "/nixdojo-logo.svg",
    type = "website",
    jsonLd
}) {
    useEffect(() => {
        const origin = window.location.origin || DEFAULT_ORIGIN;
        const canonical = new URL(path, origin).toString();
        const absoluteImage = new URL(image, origin).toString();
        const pageTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;

        document.title = pageTitle;
        upsertMeta('meta[name="description"]', { name: "description", content: description });
        upsertMeta('meta[name="robots"]', { name: "robots", content: "index,follow" });
        upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
        upsertMeta('meta[property="og:title"]', { property: "og:title", content: pageTitle });
        upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
        upsertMeta('meta[property="og:type"]', { property: "og:type", content: type });
        upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
        upsertMeta('meta[property="og:image"]', { property: "og:image", content: absoluteImage });
        upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
        upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: pageTitle });
        upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
        upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: absoluteImage });
        upsertLink("canonical", canonical);

        upsertJsonLd("site-jsonld", {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": SITE_NAME,
            "url": origin,
            "description": DEFAULT_DESCRIPTION
        });

        if (jsonLd) {
            upsertJsonLd("page-jsonld", jsonLd);
        }
    }, [description, image, jsonLd, path, title, type]);

    return null;
}
